import React, { Component, Fragment } from 'react';
import nanoid from 'nanoid';
import cn from 'classnames';
import find from 'lodash/find';
import uniq from 'lodash/uniq';
import debounce from 'lodash/debounce';

import { rerenderOnResize } from '../../decorators';
import { getUserFullName, focusChatTextArea, getRoomName, getMessageSnippet } from '../../utils/utilsChat';
import {
  isWebImage,
  getSoundexHash,
  isMobileSizeScreen,
  prefixPrevProps,
} from '../../utils/utilsGlobal';

import {
  TextareaWithFormatting,
  Dropdown,
  Tooltip,
} from '../../components';

import styles from './ChatTextArea.scss';
import uiStyles from '../../styles/ui.scss';


@rerenderOnResize()
export class ChatTextArea extends Component {

  container = React.createRef();
  textarea = React.createRef();
  inputFile = React.createRef();
  mentionsAutocomplete = React.createRef();

  state = {
    isDraggingAttachments: false,
    isMobileActionMenuOpen: false,
    mentionsAutocompleteSearchString: '',
    mentionsAutocompleteItems: [],
  };

  debouncedOnDragCancel = debounce(this.onDragCancel.bind(this), 1500);

  componentDidMount(){
    document.body.addEventListener('dragover', this.onBodyDrag);
    document.body.addEventListener('drop', this.onBodyDrop);
    document.body.addEventListener('dragleave', this.debouncedOnDragCancel);
    document.body.addEventListener('dragenter', this.debouncedOnDragCancel);
  }

  componentDidUpdate(prevProps){
    const {
      currentRoom,
      attachments,
      mustOpenWidget,
      mentionedUserIds,
      responseToMessage,
    } = this.props;

    const {
      prevCurrentRoom,
      prevAttachments,
      prevMustOpenWidget,
      prevMentionedUserIds,
      prevResponseToMessage,
    } = prefixPrevProps(prevProps);

    const shouldUpdateTextareaHeight = attachments !== prevAttachments
      || responseToMessage?.id !== prevResponseToMessage?.id
      || mentionedUserIds !== prevMentionedUserIds;

    const shouldFocusTextarea = shouldUpdateTextareaHeight
      || currentRoom?.id !== prevCurrentRoom?.id
      || mustOpenWidget !== prevMustOpenWidget;

    if (shouldUpdateTextareaHeight) {
      this.updateVerticalHeight();
    }
    if (shouldFocusTextarea) {
      focusChatTextArea();
    }
  }

  componentWillUnmount(){
    document.body.removeEventListener('dragover', this.onBodyDrag);
    document.body.removeEventListener('drop', this.onBodyDrop);
  }

  onDragCancel(){
    this.setState({ isDraggingAttachments: false });
  }

  onBodyDrag = (e) => {
    e.preventDefault();
    this.setState({ isDraggingAttachments: true });
  };

  onBodyDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    let attachments;
    if (e.dataTransfer.items) {
      attachments = Array.from(e.dataTransfer.items)
        .filter(item => item.kind === 'file')
        .map(item => item.getAsFile());
    }
    else {
      attachments = Array.from(e.dataTransfer.files);
    }
    this.addAttachments(attachments.map(file => ({ name: file.name, file })));
    this.setState({ isDraggingAttachments: false });
  };

  onTextareaChange = (e) => {
    const text = e.target.value;
    this.props.onChange({ text });
  };

  onTextareaSubmit = () => {
    const { onSubmit } = this.props;
    if (this.submitIsAvailable()) {
      onSubmit && onSubmit();
    }
  };

  submitIsAvailable = () => {
    const { text, attachments } = this.props;
    return text.trim() || attachments.length
  }

  onTextareaKeyUp = (e) => {
    const { editingOriginalMessage, onCancelEditingMessage, onStartEditingMessage } = this.props;
    const textarea = e.target;
    const textareaValue = textarea.value.trim();
    const isArrowUpPressed = e.keyCode === 38;
    const isEscPressed = e.keyCode === 27;

    if (isArrowUpPressed && !editingOriginalMessage && !textareaValue) {
      onStartEditingMessage && onStartEditingMessage();
    }
    else if (isEscPressed && editingOriginalMessage) {
      onCancelEditingMessage && onCancelEditingMessage();
    }
    else if (this.isFocusSetOnMention()) {
      const textBeforeCursor = textareaValue.substr(0, textarea.selectionStart);
      const mentionsAutocompleteSearchString = textBeforeCursor.split('@').pop();
      const mentionsAutocompleteItems = this.searchUserMentions(mentionsAutocompleteSearchString);
      this.setState({ mentionsAutocompleteItems, mentionsAutocompleteSearchString });
    }
    else {
      this.resetMentionsAutocomplete();
    }
  };

  onTextareaKeyDown = (e) => {
    const { disableSubmissionOnEnter } = this.props;
    const { mentionsAutocompleteItems } = this.state;
    const isArrowUpOrDownPressed = e.which === 38 || e.which === 40;
    const isEnterWithoutShiftPressed = e.which === 13 && !e.shiftKey;
    const isMentionsAutocompleteOpen = Boolean(mentionsAutocompleteItems.length);

    if (isMentionsAutocompleteOpen) {
      if (isEnterWithoutShiftPressed || isArrowUpOrDownPressed) {
        this.mentionsAutocomplete.current.onComponentKeydown(e);
        e.preventDefault();
      }
    }
    else if (isEnterWithoutShiftPressed && !disableSubmissionOnEnter && !this.isMobileView()) {
      this.onTextareaSubmit(); // Submitting on Enter on desktop
      e.preventDefault();
    }
  };

  isFocusSetOnMention = () => {
    const { disableMentions } = this.props;
    if (disableMentions) {
      return false;
    }
    const textarea = this.textarea.current.element;
    const textBeforeCursor = textarea.value.substr(0, textarea.selectionStart);
    return /(^|\s)@(?:[a-zа-я0-9\u00A0]+)?$/i.test(textBeforeCursor);
  };

  // TODO: replace w/ server autocompletion
  searchUserMentions = (query) => {
    const { currentRoom } = this.props;
    const normalizedQuery = query.toLowerCase().trim();
    const querySoundexHash = getSoundexHash(normalizedQuery);
    let autocompleteUsers;
    let autocompleteItems;

    autocompleteUsers = currentRoom.members;
    if (normalizedQuery) {
      autocompleteUsers = currentRoom.members.filter(member => {
        return getSoundexHash(member.firstName).startsWith(querySoundexHash) || getSoundexHash(member.lastName).startsWith(querySoundexHash);
      });
    }
    autocompleteItems = autocompleteUsers
      .map(item => {
        return {
          value: item.id,
          title: item.title || getUserFullName(item, '\u00A0'),
        }
      })
      .sort((a,b) => {
        return a.title < b.title ? -1 : 1;
      });

    const isMentioningAll = 'все'.startsWith(normalizedQuery) || 'all'.startsWith(normalizedQuery);
    if (isMentioningAll) {
      autocompleteItems.unshift({
        value: 'ALL',
        title: 'Все',
        dividerBelow: !!autocompleteUsers.length,
      });
    }
    return autocompleteItems;
  };

  resetMentionsAutocomplete = () => {
    this.setState({
      mentionsAutocompleteItems: [],
      mentionsAutocompleteSearchString: '',
    });
  };

  onMentionsAutocompleteChange = async (data) => {
    const { text, mentionedUserIds, onChange } = this.props;
    const { mentionsAutocompleteSearchString } = this.state;
    const textarea = this.textarea.current.element;

    const updatedCaretPosition = textarea.selectionStart
      - mentionsAutocompleteSearchString.length
      - 1; // "1" for the length of a single "@" symbol

    const updatedText = text.substr(0, updatedCaretPosition) + text.substr(textarea.selectionStart);
    const updatedMentionedUserIds = uniq([ ...mentionedUserIds, data.value ]);

    onChange({ text: updatedText, mentionedUserIds: updatedMentionedUserIds }, () => {
      this.resetMentionsAutocomplete();
      textarea.setSelectionRange(updatedCaretPosition, updatedCaretPosition);
    });
  };

  getMentionedUsersList = (mentionedUserIds) => {
    const { currentRoom } = this.props;
    return mentionedUserIds.map(userId => {
      const id = userId;
      let title;
      if (id === 'ALL') {
        title = 'Все';
      }
      else {
        const user = find(currentRoom.members, { id }) || {};
        title = this.isMobileView() ? user.firstName : getUserFullName(user);
      }
      return { id, title };
    });
  };

  onMentionedUserRemove = (userId) => {
    const { mentionedUserIds, onChange } = this.props;
    onChange({
      mentionedUserIds: mentionedUserIds.filter(id => id !== userId)
    });
  };

  onRemoveReplyTo = () => {
    this.props.onChange({ responseToMessageId: null });
  };

  extractImageMetaData = async (imageUrl) => {
    return new Promise(resolve => {
      const image = new Image();
      image.onload = () => {
        resolve({
          width: image.width,
          height: image.height,
          thumbnailUrl: imageUrl,
        });
      };
      image.onerror = () => {
        resolve({
          width: 0,
          height: 0,
          thumbnailUrl: '',
        });
      };
      image.src = imageUrl;
    });
  };

  addAttachments = async attachments => {
    const newAttachments = attachments.map(async attachment => {
      const id = nanoid();
      const fileBlobUrl = URL.createObjectURL(attachment.file);
      if (isWebImage(attachment.file.type)) {
        return {
          ...attachment,
          ...await this.extractImageMetaData(fileBlobUrl),
          id,
        };
      }
      else {
        return { ...attachment, id };
      }
    });
    const updatedAttachments = [
      ...this.props.attachments.filter(attachment => !attachment.editingPlaceholder),
      ...await Promise.all(newAttachments)
    ];
    this.props.onChange({ attachments: updatedAttachments });
  };

  removeAttachment = attachmentId => {
    const { attachments, onChange } = this.props;
    const updatedAttachments = attachments.filter(item => item.id !== attachmentId);
    onChange({ attachments: updatedAttachments });
  };

  handleAttachmentPaste = e => {
    let fileCounter = 0;
    const clipboardItems = Array.from(e?.clipboardData?.items) || [];

    const attachments = clipboardItems
      .map(clipboardItem => {
        if (clipboardItem.kind === 'file') {
          const file = clipboardItem.getAsFile();
          if (file) {
            const fileNameExtension = file.name.split('.').pop();
            const customFileName = 'Буфер обмена '
              + new Date().toLocaleString()
              + (fileCounter ? ` (${fileCounter + 1})` : '')
              + '.'
              + fileNameExtension;
            const customFile = new File([file], customFileName, { type: file.type });
            const attachmentName = isWebImage(file.type)
              ? 'Изображение вставлено из буфера обмена'
              : 'Файл вставлен из буфера обмена';

            fileCounter++;

            return {
              name: attachmentName,
              file: customFile,
            };
          }
        }
      })
      .filter(item => item);

    if (attachments.length) {
      e.preventDefault();
      this.addAttachments(attachments);
    }
  };

  onInputFileChange = (e) => {
    const attachments = Array.from(e.target.files).map(file => ({
      name: file.name,
      file,
    }));
    this.addAttachments(attachments);
    this.inputFile.current.value = '';
  };

  toggleMobileActionMenu = () => {
    this.setState({ isMobileActionMenuOpen: !this.state.isMobileActionMenuOpen });
  };

  closeMobileActionMenu = () => {
    this.setState({ isMobileActionMenuOpen: false });
  };

  updateVerticalHeight = () => {
    const { onVerticalResize } = this.props;
    setTimeout(() => {
      onVerticalResize && onVerticalResize(this.container.current.offsetHeight);
    });
  };

  onOpenWidgetButtonClick = () => {
    const { mustOpenWidget, onChange } = this.props;
    onChange({ mustOpenWidget: !mustOpenWidget });
  };

  onRequestScreenshotButtonClick = () => {
    const { onRequestScreenshotClick } = this.props;
    if (confirm('Запросить скриншот экрана пользователя?')) {
      onRequestScreenshotClick && onRequestScreenshotClick();
      focusChatTextArea();
    }
  };

  getMustOpenWidgetTooltip = () => {
    const { mentionedUserIds, disableMentions, currentRoom } = this.props;
    let text = 'Раскрыть окно чата';

    if (!disableMentions) {
      if (mentionedUserIds?.length) {
        const mentionedNames = this.getMentionedUsersList(mentionedUserIds).map(item => item.title);
        text = `${text}\n только у этих клиентов:\n${mentionedNames.join(',\n')}`;
      }
      else {
        text = `${text} у всех\n клиентов ${getRoomName(currentRoom)}`;
      }
    }
    return text;
  };

  isMobileView = () => {
    const { disableMobileView } = this.props;
    return isMobileSizeScreen() && !disableMobileView;
  };

  getEnabledActions = () => {
    const { hideActions } = this.props;
    const enabledActions = {};
    const availableActions = {
      OPEN_WIDGET: true,
      ATTACH_FILES: true,
      REQUEST_SCREENSHOT: true,
      SEND_MESSAGE: true
    };
    for (let action in availableActions) {
      if (!hideActions?.[action]) {
        enabledActions[action] = true;
      }
    }
    return enabledActions;
  };

  render(){
    const {
      className,
      text,
      attachments,
      responseToMessage,
      mentionedUserIds,
      isCurrentRoomCaseOpen,
      onCaseCloseClick,
      onCreateTaskClick,
      mustOpenWidget,
      placeholder,
      disableBottomButtons,
      disableMobileView,
    } = this.props;

    const {
      isDraggingAttachments,
      isMobileActionMenuOpen,
      mentionsAutocompleteItems,
    } = this.state;

    const isMobileView = this.isMobileView();
    const enabledActions = this.getEnabledActions();
    const textareaPaddingRight = 55 * Object.keys(enabledActions).length;
    const submitIsAvailable = this.submitIsAvailable();

    return (
      <div className={cn({
        [className]: true,
        [styles.container]: true,
        [styles.containerMobile]: isMobileView,
        [styles.containerEnableMobileView]: !disableMobileView,
      })} ref={this.container}>

        {(isMobileView && isMobileActionMenuOpen) && (
          <ul className={styles.mobileActions}>
            <li className={styles.mobileActionsItem} onClick={() => {
              this.onRequestScreenshotButtonClick();
              this.closeMobileActionMenu();
            }}>Запросить скриншот</li>
            <li className={styles.mobileActionsItem} onClick={() => {
              this.inputFile.current.click();
              this.closeMobileActionMenu();
            }}>Прикрепить файлы</li>
            <li className={styles.mobileActionsItem} onClick={() => {
              onCreateTaskClick && onCreateTaskClick();
              this.closeMobileActionMenu();
            }}>Поставить задачу</li>
            <li className={cn({
              [styles.mobileActionsItem]: true,
              [styles.mobileActionsItemDisabled]: !isCurrentRoomCaseOpen,
            })} onClick={() => {
              onCaseCloseClick && onCaseCloseClick();
              this.closeMobileActionMenu();
            }}>{isCurrentRoomCaseOpen ? 'Завершить кейс' : 'Кейс завершен'}</li>
          </ul>
        )}

        {Boolean(mentionedUserIds?.length) && (
          <ul className={styles.mentions}>
            {this.getMentionedUsersList(mentionedUserIds).map(user => (
              <li className={styles.mentionsUser} key={user.id}>
                {user.title}
                <i className={cn(styles.mentionsUserRemove, 'icon-close-thick')}
                  onClick={() => this.onMentionedUserRemove(user.id)}/>
              </li>
            ))}
          </ul>
        )}

        {Boolean(responseToMessage) && (
          <div className={styles.reply}>
            <i className={cn(styles.replyIcon, 'icon-reply-right')}/>
            <div className={styles.replyMessage}>
              {!isMobileView && (
                <Fragment>
                  <b>Ответ:</b>&nbsp;{getUserFullName(responseToMessage.sender)}&nbsp;
                </Fragment>
              )}
              {getMessageSnippet(responseToMessage)}
            </div>
            <i className={cn(styles.replyRemove, 'icon-close-thick')}
              onClick={this.onRemoveReplyTo}
              tabIndex={0}/>
          </div>
        )}

        <div className={styles.input}>
          {isMobileView && (
            <Fragment>
              <button className={cn(styles.mobileSubmit, 'icon-submit')} onClick={this.onTextareaSubmit}/>
              <button className={cn(styles.mobileActionsIcon, 'icon-ellipsis')} onClick={this.toggleMobileActionMenu}/>
            </Fragment>
          )}

          <Dropdown ref={this.mentionsAutocomplete}
            onChange={this.onMentionsAutocompleteChange}
            dontKeepSelectedOption={true}
            isOpen={mentionsAutocompleteItems.length}
            items={mentionsAutocompleteItems}
            overrideClassNames={{
              container: styles.mentionsAutocomplete,
              list: styles.mentionsAutocompleteList,
              item: styles.mentionsAutocompleteItem,
              itemFocused: styles.mentionsAutocompleteItemFocused,
            }}/>

          <TextareaWithFormatting
            id="ChatTextAreaField"
            className={styles.inputTextarea}
            style={{ paddingRight: textareaPaddingRight }}
            placeholder={placeholder || 'Ответ клиенту...'}
            value={text}
            ref={this.textarea}
            minRows={isMobileView ? 1 : 2}
            maxRows={isMobileView ? 4 : 10}
            onHeightChange={this.updateVerticalHeight}
            onPaste={this.handleAttachmentPaste}
            onChange={this.onTextareaChange}
            onKeyDown={this.onTextareaKeyDown}
            onKeyUp={this.onTextareaKeyUp}/>

          <div className={styles.inputActionsList}>
            {enabledActions.OPEN_WIDGET && (
              <div className={cn({
                [styles.inputAction]: true,
                [styles.inputActionOpenWidgetActive]: mustOpenWidget,
              })}>
                <Tooltip title={this.getMustOpenWidgetTooltip()}>
                  <button className={cn(styles.inputActionButton, uiStyles.buttonSecondarySmall)}
                    onClick={this.onOpenWidgetButtonClick}>
                    <i className="icon-arrow-up"/>
                  </button>
                </Tooltip>
              </div>
            )}

            {enabledActions.ATTACH_FILES && (
              <div className={styles.inputAction}>
                <input
                  className={styles.inputActionFileUploadInput}
                  id="ChatTextAreaFileUpload"
                  type="file"
                  ref={this.inputFile}
                  multiple={true}
                  onChange={this.onInputFileChange}/>

                <Tooltip title="Прикрепить файлы">
                  <label className={cn(styles.inputActionButton, uiStyles.buttonSecondarySmall)}
                    htmlFor="ChatTextAreaFileUpload">
                    <i className="icon-file"/>
                  </label>
                </Tooltip>
              </div>
            )}

            {enabledActions.REQUEST_SCREENSHOT && (
              <div className={styles.inputAction}>
                <Tooltip title="Запросить скриншот экрана">
                  <button className={cn(styles.inputActionButton, uiStyles.buttonSecondarySmall)}
                          onClick={this.onRequestScreenshotButtonClick}>
                    <i className="icon-screenshot"/>
                  </button>
                </Tooltip>
              </div>
            )}

            {enabledActions.SEND_MESSAGE && (
              <div className={cn({
                [styles.inputAction]: true,
                [styles.inputActionOpenWidgetActive]: submitIsAvailable,
              })}>
                <Tooltip title="Отправить сообщение">
                  <button className={cn(styles.inputActionButton, uiStyles.buttonSecondarySmall)}
                          onClick={this.onTextareaSubmit}>
                    <i className="icon-submit"/>
                  </button>
                </Tooltip>
              </div>
            )}

          </div>
        </div>

        {Boolean(attachments.length) && (
          <ul className={styles.attachmentList}>
            {attachments.map(attachment => (
              <li key={attachment.id} className={styles.attachmentItem}>
                <i className={cn(styles.attachmentIcon, 'icon-file')}/>
                <span className={styles.attachmentFileName}>{attachment.name}</span>
                <i className={cn(styles.attachmentRemove, 'icon-close-thick')}
                  tabIndex={0}
                  onClick={() => this.removeAttachment(attachment.id)}>
                </i>
              </li>
            ))}
          </ul>
        )}

        {isDraggingAttachments && (
          <div className={styles.draggableArea}>
            <div className={styles.draggableInner}>
              <i className={cn(styles.draggableIcon, 'icon-file')}/>
              <div>Перетащите сюда файлы для загрузки</div>
            </div>
          </div>
        )}

        {!disableBottomButtons && (
          <div className={styles.actions}>
            <button className={styles.actionsButton} onClick={onCreateTaskClick}>
              Поставить задачу
            </button>
            <button className={styles.actionsButton}
              onClick={onCaseCloseClick}
              disabled={!isCurrentRoomCaseOpen}>
              {isCurrentRoomCaseOpen ? 'Завершить кейс' : 'Кейс завершен'}
            </button>
          </div>
        )}
      </div>
    );
  }
}
